import { FC } from "react"
import { Autocomplete, Stack, TextField } from "@mui/material"
import { useLazyClientsQuery, useLazyRolesQuery } from "../../store"
import { useInfiniteSelectList } from "@/core/hooks"
import { infiniteListboxProps } from "@/core/components"
import { Userbot } from "../../interfaces"

interface IProps {
  executor: "client" | "role"
  clientId: string
  role: string
  onClientChange: (value: string) => void
  onRoleChange: (value: string) => void
}

/** Не-RHF селекты исполнителя для конструктора (работают на обычном state). */
export const ClientSelectRaw: FC<IProps> = ({
  executor,
  clientId,
  role,
  onClientChange,
  onRoleChange,
}) => {
  // Оба хука — до условного return (rules-of-hooks).
  const [lazyClients] = useLazyClientsQuery()
  const clientsList = useInfiniteSelectList<Userbot.Client>({
    fetchPage: (p) => lazyClients(p).unwrap(),
    getId: (c) => c.id,
  })
  const [lazyRoles] = useLazyRolesQuery()
  const rolesList = useInfiniteSelectList<Userbot.Role>({
    fetchPage: (p) => lazyRoles(p).unwrap(),
    getId: (r) => r.name,
  })

  if (executor === "client") {
    const value = clientsList.items.find((c) => c.id === clientId) ?? null
    return (
      <Stack>
        <Autocomplete<Userbot.Client>
          options={clientsList.items}
          value={value}
          onChange={(_, v) => onClientChange(v?.id ?? "")}
          getOptionLabel={(o) =>
            `${o.name}${o.isAuthorized ? "" : " (не авторизован)"}`
          }
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={(o) => o}
          onInputChange={(_, v, reason) => {
            if (reason === "input") clientsList.setSearch(v)
          }}
          renderInput={(params) => (
            <TextField {...params} label="Клиент" required />
          )}
          {...infiniteListboxProps({
            hasMore: clientsList.hasMore,
            isFetching: clientsList.isFetching,
            onLoadMore: clientsList.loadMore,
          })}
          renderOption={(props, option, state) =>
            [
              props,
              `${option.name}${option.isAuthorized ? "" : " (не авторизован)"}`,
              state,
            ] as React.ReactNode
          }
        />
      </Stack>
    )
  }

  const roleNames = rolesList.items.map((r) => r.name)
  return (
    <Autocomplete<string, false, false, true>
      freeSolo
      options={roleNames}
      value={role}
      onInputChange={(_, v, reason) => {
        onRoleChange(v)
        if (reason === "input") rolesList.setSearch(v)
      }}
      filterOptions={(o) => o}
      renderInput={(params) => <TextField {...params} label="Роль" required />}
      {...infiniteListboxProps({
        hasMore: rolesList.hasMore,
        isFetching: rolesList.isFetching,
        onLoadMore: rolesList.loadMore,
      })}
      renderOption={(props, o, state) => [props, o, state] as React.ReactNode}
    />
  )
}
